import AutoHeightTextarea from './components/AutoHeightTextarea.js'
const { createApp, ref, computed, onMounted, onBeforeUnmount } = Vue

// メールアドレスの正規表現
const REG_EMAIL = /^[a-z\d][\w.-]*@[\w.-]+\.[a-z\d]+$/i

// 無効なドメインリスト
const BLOCKED_DOMAINS = [
  'gmail.com',
  'yahoo.co.jp',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'docomo.ne.jp',
  'ezweb.ne.jp',
  'au.com',
  'softbank.ne.jp',
]

createApp({
  components: { AutoHeightTextarea },
  setup() {
    // フォームの定義
    const forms = ref(FORMS)

    // 入力値
    const inputs = ref(getInitInputs())

    // 個人情報の取り扱い
    const isPrivacyPolicyAccepted = ref(false)

    // メール本文用データ
    const zip = ref(null)

    // 送信中
    const isBusy = ref(false)

    // エラーメッセージ
    const errors = ref([])

    // システムエラー
    const isSystemError = ref(false)

    // zipデータの読み込み完了ステータス
    const isReady = computed(() => !!zip.value)

    // 送信完了オブジェクト
    const results = ref(null)

    // ボトムCTA
    const formWrapper = ref(null)
    const ctaWrapper = ref(null)
    const hideFixedBottom = ref(false)

    // zipデータを読み込む
    getZipData().then((value) => {
      zip.value = value
    })

    // メールテキストを返却する
    async function getMailText(fileName) {
      return await zip.value.file(fileName).async('text')
    }

    // フォームの初期値を返却する
    function getInitInputs() {
      return FORMS.reduce((acc, item) => {
        return { ...acc, [item.key]: '' }
      }, {})
    }

    // フォームを初期化する
    function refresh() {
      inputs.value = getInitInputs()
      results.value = null
    }

    // フォーム送信処理
    async function submit(type) {
      isBusy.value = true
      try {
        // メールデータを取り出す
        const mail = await getMailText(`${type}.txt`)

        // 入力データのバリデーション
        if (!validateInputData()) {
          return
        }

        const data = {
          ...getURLParams(),
          ...inputs.value,
        }

        // reCAPTCHAのtokenを取得する
        const token = await getRecaptchaToken()

        // データを送信する
        results.value = await post({ mail, data, token })

        // 各種計測
        try {
          // GTM
          const thanksTitle = `送信完了 | ${results.value.api.formSignature} - ${results.value.api.form}`
          const thanksSlug = results.value.thanks.slug
          gtmSendPageView(thanksTitle, thanksSlug)

          // 配配メール
          hhmConversion()

          // Meta Pixel Code
          switch (type) {
            case 'download':
              fbq('trackCustom', 'VirtualPageView', {
                'page_path': window.location.pathname + 'thanks-dl.html'
              })
              fbq('trackCustom', 'DownloadComplete')
              break

            case 'inquiry':
              fbq('trackCustom', 'VirtualPageView', {
                'page_path': window.location.pathname + 'thanks-inq.html'
              })
              fbq('trackCustom', 'InquiryComplete')
              break
          }
        } catch (err) {
          // 計測失敗
        }

      } catch (err) {
        // 送信失敗
        isSystemError.value = true
      } finally {
        isBusy.value = false
      }
    }

    // 入力値のバリデーションを行う
    function validateInputData() {
      // エラーをリセットする
      errors.value = []

      // 必須項目
      forms.value.forEach((form) => {
        // 空白を除去する
        inputs.value[form.key] = inputs.value[form.key].trim()

        // 必須項目
        if (!!form.requierd && !inputs.value[form.key]) {
          errors.value.push(`${form.label}を入力してください。`)
        }

        if (inputs.value[form.key] && form.rules) {
          // email
          if (form.rules.includes('email')) {
            if (!REG_EMAIL.test(inputs.value[form.key])) {
              errors.value.push(`${form.label}に誤りがあります。`)
            }
            { // 禁止ドメインのチェック
              const domain = inputs.value[form.key].split("@")[1]?.toLowerCase()
              if (BLOCKED_DOMAINS.includes(domain)) {
                errors.value.push('会社のメールアドレス（独自ドメイン）をご入力ください。Gmail等のフリーメールやキャリアメールはご利用になれません。')
              }
            }
          }
        }
      })

      if (isPrivacyPolicyAccepted.value === false) {
        errors.value.push('個人情報の取り扱いに同意してください。')
      }

      return errors.value.length === 0
    }

    // FAQ
    const faqs = ref([
      {
        q: '事前に売却額のお見積りをいただくことは可能ですか？',
        a: 'もちろん可能です。買取前に「買取依頼票」という専用フォーマットをお送りいたしますので、必要項目を記載いただき、お見積りをさせていただきます。',
        open: false,
      },
      {
        q: '回収された後のデータはどうなりますか？',
        a: '商品お預かり後、データ消去(物理破壊含む)を行ってからの査定となります。万が一ご返却する際はデータ消去後となり、データ保証は出来かねますのでご了承ください。',
        open: false,
      },
      {
        q: 'パスワードやロックがかかったままでも買取可能ですか？',
        a: 'BIOS、UEFI、HDD等にパスワードやロックがかかっている場合、データの消去作業が行えず、査定額に大幅な減額が発生いたします。必ず事前に解除していただいた上でご依頼ください。',
        open: false,
      },
      {
        q: 'データ消去や回収にかかる費用は？',
        a: '回収費用は買取金額から相殺し、データ消去費用は買取金額内での対応となりますので、別途費用が発生することは原則ございません。',
        open: false,
      },
    ])

    function faqTrBeforeEnter(el) {
      el.style.height = '0'
      el.style.overflow = 'hidden'
    }

    function faqTrEnter(el) {
      const height = el.scrollHeight

      requestAnimationFrame(() => {
        el.style.height = `${height}px`
      })
    }

    function faqTrAfterEnter(el) {
      el.style.height = 'auto'
      el.style.overflow = ''
    }

    function faqTrBeforeLeave(el) {
      el.style.height = `${el.scrollHeight}px`
      el.style.overflow = 'hidden'
    }

    function faqTrLeave(el) {
      // 一度現在の高さを確定させる
      el.offsetHeight

      requestAnimationFrame(() => {
        el.style.height = '0'
      })
    }

  const isVisible = (el) => {
    if (!el) return false

    const rect = el.getBoundingClientRect()

    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0
    )
  }

    const updateVisibility = () => {
      if (!formWrapper.value) return

      const formRect = formWrapper.value.getBoundingClientRect()

      const isFormVisible = formRect.top < window.innerHeight && formRect.bottom > 0

      const isScrolledPastForm = formRect.bottom <= 0

      const isCtaVisible = isVisible(ctaWrapper.value)

      console.log({
        isFormVisible,
        isScrolledPastForm,
        isCtaVisible,
      })

      hideFixedBottom.value = isFormVisible || isScrolledPastForm || isCtaVisible
    }

    onMounted(() => {
      updateVisibility()
      window.addEventListener('scroll', updateVisibility, { passive: true })
      window.addEventListener('resize', updateVisibility)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', updateVisibility)
      window.removeEventListener('resize', updateVisibility)
    })

    return {
      isReady,
      isBusy,
      forms,
      inputs,
      isPrivacyPolicyAccepted,
      errors,
      isSystemError,
      results,
      submit,
      refresh,
      faqs,
      faqTrBeforeEnter,
      faqTrEnter,
      faqTrAfterEnter,
      faqTrBeforeLeave,
      faqTrLeave,
      formWrapper,
      ctaWrapper,
      hideFixedBottom,
    }
  }
}).mount('#app')


// URLパラメータを取得して返却する
function getURLParams() {
  const params = new URLSearchParams(location.search)
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
    ptnrref: params.get('ptnrref') || '',
  }
}

// ZIPファイルからデータを取り出して返却する
function getZipData() {
  return new Promise(async (resolve, reject) => {
    const response = await fetch('./data.zip')
    if (!response.ok) {
      console.log('データの読み込みに失敗しました')
      return reject(response.statusText)
    }
    const buffer = await response.arrayBuffer()
    const zip = await JSZip.loadAsync(buffer)
    resolve(zip)
  })
}

// reCAPTCHAのtokenを取得する
async function getRecaptchaToken() {
  return new Promise((resolve, reject) => {
    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
        resolve(token)
      } catch (err) {
        console.log(err)
        reject('reCAPTCHAエラーが発生しました。')
      }
    })
  })
}

// APIにデータを送信する
async function post({ mail, data, token }) {
  try {
    const response = await fetch('/api/v1/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mail, data, token })
    })

    if (!response.ok) {
      throw new Error('error')
    }

    return await response.json()
  } catch (err) {
    console.log(err)
    throw new Error('フォームの送信に失敗しました。')
  }
}

// GTM
function gtmSendPageView(thanksTitle, thanksSlug) {
  const url = new URL(location.href)
  const directory = url.pathname.replace(/\/[^/]+$/, '')

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'cvPageview',
    pageLocation: `https://${url.host}${directory}${thanksSlug}`,
    pageTitle: thanksTitle,
    pageReferrer: location.href,
  })
}

// 配配メールCV
function hhmConversion() {
    const e = 'e0933986f5b1879c4e667a1cbc8b593c'
    const t = '0b3ed08f587e32918cd06e46fcdd4a1c'
    const n = Date.now().toString()

    if (
        !document.cookie.split(';').every(function(cookie) {
            return cookie.indexOf(
                '_hmcv_e0933986f5b1879c4e667a1cbc8b593c=0b3ed08f587e32918cd06e46fcdd4a1c'
            ) === -1;
        })
    ) {
        const c = document.createElement('script')

        c.type = 'text/javascript';
        c.src = 'https://contents-lb01.hm-f.jp/cv.js?t=' + n
        c.async = true
        c._hmServer = 'https://a18.hm-f.jp'
        c._hmCmTagCode = e
        c._hmCvTagCode = t

        document.head.appendChild(c)
    }
}
